/**
 * TASK-0244 (FR-NEEDS): the "being reviewed by" marker, stamped by every screen an invoice is reviewed
 * on: the inbox review (`ReviewPane`) and the PO's invoice screen (`InvoiceScreen`, review R1-4: owner
 * direction C43 makes "Scan invoice" from the PO the main path).
 *
 * Opening an import that can still be recorded stamps this person on it once per screen; the answer
 * names whoever had it open before. Display only: a failure changes nothing on the review.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { importKeys } from './api';
import type { ReviewingMarker } from './types';

/** The import can still be reviewed: read, not recorded, not dismissed. */
export function reviewableImport(
  detail:
    | {
        status: string;
        confirmed: boolean;
        dismissedAt?: string | null;
        vendorInvoiceId?: number | null;
      }
    | null
    | undefined
): boolean {
  return (
    !!detail &&
    detail.status === 'matched' &&
    !detail.confirmed &&
    !detail.dismissedAt &&
    detail.vendorInvoiceId == null
  );
}

/** Stamps the marker once per import while it is reviewable; answers this person's marker. */
export function useReviewingMarker(
  importId: number | null,
  reviewable: boolean
): ReviewingMarker | null {
  const queryClient = useQueryClient();
  const [marker, setMarker] = useState<ReviewingMarker | null>(null);
  const markedFor = useRef<number | null>(null);
  useEffect(() => {
    if (importId === null || !reviewable || markedFor.current === importId) return;
    markedFor.current = importId;
    Promise.resolve()
      .then(() =>
        api.post<ReviewingMarker>(`/ai/invoice-imports/${importId}/reviewing`, {})
      )
      .then((answer) => {
        if (!answer) return;
        setMarker(answer);
        void queryClient.invalidateQueries({ queryKey: [...importKeys.all, 'list'] });
      })
      .catch(() => {
        // The marker is a courtesy; the review works without it.
      });
  }, [importId, reviewable, queryClient]);
  // A marker answered for another import (the pane moved on) says nothing about this one.
  return marker && marker.id === importId ? marker : null;
}
